import { generateGuid } from "@1js/guid";
import type { ProductType } from "@1js/pl-card-components";
import { WXPProducts } from "@1js/pl-types";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { buildCoreHeaders } from "../utils/header";
import type { SendApiActionEventParameter } from "../utils/Telemetry";
import type { HttpStatusCode } from "../utils/Telemetry/config";
import { getSupportServiceDatacenter, logApiError } from "./client-utils";
import { authAwareFetch } from "./fetch";

/**
 * Sample Response,
 * type documented on https://eng.ms/docs/experiences-devices/opg/office-ai/app-copilot/onboarding/handoff
 * HTTP/1.1 200 OK
 * Content-type: application/json
 * {
 *   "responses": [{
 *     "destinationApp": "word",
 *     "language": "en-us",
 *     "generatedLinks": [{
 *       "appPlatform": "Web",
 *       "platformHandoffUrl": "<web url>"
 *     }],
 *     "message": "<user display message>",
 *     "H_": {
 *       "T_": "AugLoop_HandoffSkill_HandoffOutboundMessage",
 *       "B_": ["AugLoop_Core_Annotation"]
 *     },
 *     "M_": { "seq": 2 }
 *   }]
 * }
 */
export interface IGetAnnotationsResponseMessage {
  destinationApp: string;
  language: string;
  generatedLinks: {
    appPlatform: string;
    platformHandoffUrl: string;
  }[];
  message: string;
}

export interface IHandoffErrorResponseMessage {
  error: string;
}

export type HandoffResponse =
  | IGetAnnotationsResponseMessage
  | IHandoffErrorResponseMessage;

export const handoffAsync = async (
  host: string,
  locale: string,
  product: ProductType | string,
  query: string,
  queryId: string,
  onSendApiAction: (params: SendApiActionEventParameter) => void,
  scenarioName?: string,
  documentUrl?: string,
  isWebUrl?: boolean,
  context?: string,
  msalContext?: IPublicClientApplication,
  destinationComponent?: string
): Promise<HandoffResponse> => {
  if (!WXPProducts.has(product)) {
    throw new Error("UnknownDestination");
  }

  const logicalId = generateGuid();
  const startTimer = Date.now();

  let body: object = {
    queryId,
    destinationApp: product,
    destinationDocUrl: documentUrl,
    destinationDocUrlPlatform: isWebUrl ? "Web" : undefined,
    handoffStartTime: Date.now(),
    language: locale,
    manualSendPrompt: true,
    scenarioName: scenarioName || "new",
    query,
    destinationComponent,
    sourceApp: "Copilot Lab",
  };

  if (context) {
    body = {
      ...body,
      context,
    };
  }

  const serviceUrl = `https://${host}/${locale}/api/promptsclient/handoff/augloop`;
  const headers = await buildCoreHeaders(msalContext, logicalId);
  headers.append(
    "RequestVerificationToken",
    document
      ?.querySelector('input[name="__RequestVerificationToken"]')
      ?.getAttribute("value") ?? ""
  );
  onSendApiAction({
    apiName: "HandoffAugloop",
    isInit: true,
    traceId: logicalId,
  });
  try {
    const response = await authAwareFetch(serviceUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    onSendApiAction({
      apiName: "HandoffAugloop",
      status: response.status as HttpStatusCode,
      isInit: false,
      isFromCache: false,
      supportServiceLatency: Date.now() - startTimer,
      supportServiceDatacenter: getSupportServiceDatacenter(response.headers),
      traceId: response.headers.get("x-datasource-traceid") || logicalId,
    });

    return (await response.json()) as IGetAnnotationsResponseMessage;
  } catch (error) {
    logApiError(
      "HandoffAugloop",
      error as Error,
      logicalId,
      Date.now() - startTimer,
      onSendApiAction
    );

    throw error;
  }
};
